import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  Button,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { getCreditosDetalle, type CreditosDetalle } from '../api/usage.js';
import { useUsageStore } from '../stores/usageStore.js';
import {
  NOTA_EQUIVALENCIA,
  enCreditos,
  etiquetaCreditos,
  formatCreditos,
  formatTokens,
  pastelDelSaldo,
  promediosDe,
  saldoEnTokens,
} from '../lib/creditos.js';
import { controlDelHeader } from './controlDelHeader.js';

/**
 * "N créditos" con desglose al hacer clic (1 crédito = 10,000 tokens).
 *
 * Es un <button> con aria-haspopup="dialog" y aria-expanded. En escritorio abre un
 * Popover (foco al contenido, Esc cierra y devuelve el foco al botón); en móvil (<640 px)
 * abre una hoja inferior (Drawer) con la misma información. El desglose muestra
 * créditos Y tokens, el consumo por función (sin nombres de modelo ni de proveedor) y la
 * nota de equivalencia. Los datos de la hoja vienen de /api/v1/usage y el detalle se pide
 * al abrir (caché de 30 s).
 */

const useStyles = makeStyles({
  btn: {
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    color: 'inherit',
    padding: '5px 10px',
    minHeight: '34px',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontVariantNumeric: 'tabular-nums',
    transitionProperty: 'background',
    transitionDuration: '120ms',
    ':hover': { background: 'var(--bg-hover)' },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '2px' },
    '@media (max-width: 639px)': { minHeight: '44px', minWidth: '44px' },
  },
  /**
   * Chip del header = el del hub (chip de créditos de opus.com.mx/public/app.html): rectángulo
   * de radio 8px con borde fino, pastel a la izquierda y «812 créditos» semibold; mismo alto
   * que los demás controles del header (controlDelHeader). En celular solo el pastel y la cifra.
   */
  pill: {
    ...controlDelHeader,
    '@media (max-width: 639px)': { padding: '0 10px', gap: '6px' },
  },
  /** Pastel: % disponible (bolsa + recargas); color del semáforo de uso. */
  dot: {
    width: '11px',
    height: '11px',
    borderRadius: '50%',
    flexShrink: 0,
    background: 'conic-gradient(var(--cr-pie) 0 var(--cr-pct), var(--border-strong) 0)',
  },
  unidad: {
    '@media (max-width: 639px)': { display: 'none' },
  },
  pop: {
    width: 'min(380px, calc(100vw - 24px))',
    maxHeight: 'min(80vh, 640px)',
    overflowY: 'auto',
    padding: 'var(--space-l) var(--space-xl)',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
  title: {
    margin: 0,
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-300)',
    color: 'var(--text-primary)',
    ':focus': { outline: 'none' },
  },
  sub: { margin: '2px 0 8px', fontSize: 'var(--fs-100)', color: 'var(--text-tertiary)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-200)', fontVariantNumeric: 'tabular-nums' },
  th: {
    textAlign: 'right',
    fontSize: 'var(--fs-100)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    padding: '4px 0',
  },
  thLeft: { textAlign: 'left' },
  td: { textAlign: 'right', padding: '4px 0', color: 'var(--text-primary)', fontWeight: 600 },
  tdLeft: { textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 400 },
  total: { borderTop: '1px solid var(--border-subtle)' },
  bar: {
    height: '6px',
    borderRadius: '999px',
    background: 'var(--bg-raised)',
    overflow: 'hidden',
    margin: '8px 0 2px',
  },
  barFill: { height: '100%', borderRadius: '999px', background: 'var(--accent)' },
  barWarn: { background: 'var(--warn)' },
  barBad: { background: 'var(--bad)' },
  barNote: { margin: 0, fontSize: 'var(--fs-100)', color: 'var(--text-tertiary)' },
  section: {
    margin: '14px 0 4px',
    fontSize: 'var(--fs-100)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
  },
  eq: { margin: 0, paddingLeft: '16px', fontSize: 'var(--fs-200)', color: 'var(--text-secondary)', lineHeight: 1.5 },
  note: { margin: '12px 0 0', fontSize: 'var(--fs-100)', color: 'var(--text-tertiary)', lineHeight: 1.45 },
  actions: { marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' },
  muted: { fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' },
  drawerBody: { paddingBottom: '24px' },
});

function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

const CACHE_MS = 30_000;
let detalleCache: { at: number; data: CreditosDetalle } | null = null;

function useDetalle(open: boolean): { detalle: CreditosDetalle | null; loading: boolean } {
  const [detalle, setDetalle] = useState<CreditosDetalle | null>(detalleCache?.data ?? null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (detalleCache && Date.now() - detalleCache.at < CACHE_MS) {
      setDetalle(detalleCache.data);
      return;
    }
    let alive = true;
    setLoading(true);
    getCreditosDetalle()
      .then((d) => {
        detalleCache = { at: Date.now(), data: d };
        if (alive) setDetalle(d);
      })
      .catch(() => undefined)
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open]);
  return { detalle, loading };
}

const fmtFecha = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', timeZone: 'UTC' });
function renuevaTexto(renueva?: string | null, monthEnd?: string | null): string {
  const iso = renueva || monthEnd;
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `Se renueva el ${fmtFecha.format(d)}.`;
}

/** Contenido del desglose (popover y hoja). */
export function CreditosDesglose({
  onBuy,
  tituloRef,
  onClose,
  ocultarTitulo = false,
}: {
  onBuy?: () => void;
  tituloRef?: React.Ref<HTMLHeadingElement>;
  onClose?: () => void;
  /** La hoja inferior ya trae su propio encabezado. */
  ocultarTitulo?: boolean;
}) {
  const s = useStyles();
  const usage = useUsageStore((st) => st.data);
  const { detalle, loading } = useDetalle(true);
  const saldo = saldoEnTokens(usage);
  const promedios = promediosDe(usage);
  if (!usage || !saldo) return <p className={s.muted}>Saldo no disponible por ahora.</p>;

  const barClass = saldo.pct >= 90 ? s.barBad : saldo.pct >= 75 ? s.barWarn : '';
  const funciones = (detalle?.por_funcion ?? []).filter((f) => f.tokens > 0).slice(0, 6);
  const recargas = detalle?.recargas ?? [];
  const mesTxt = renuevaTexto(detalle?.renueva, usage.tokens.month_end);

  const filas: Array<[string, number, 'saldo' | 'desglose', boolean?]> = [
    ['Bolsa del mes', saldo.bolsa, 'saldo'],
    ['Usado', saldo.usado, 'desglose'],
    ['Recargas disponibles', saldo.recargas, 'saldo'],
    ['Disponible', saldo.disponible, 'saldo', true],
  ];

  return (
    <div>
      {!ocultarTitulo && (
        <div className={s.head}>
          <h2 className={s.title} tabIndex={-1} ref={tituloRef}>
            Tus créditos
          </h2>
          {onClose && (
            <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Cerrar" onClick={onClose} />
          )}
        </div>
      )}
      <p className={s.sub}>
        Plan {usage.plan_display || usage.plan}
        {mesTxt ? ` · ${mesTxt}` : ''}
      </p>

      <table className={s.table}>
        <caption style={{ position: 'absolute', left: '-9999px' }}>Saldo en créditos y tokens</caption>
        <thead>
          <tr>
            <th scope="col" className={mergeClasses(s.th, s.thLeft)}>
              Concepto
            </th>
            <th scope="col" className={s.th}>
              créditos
            </th>
            <th scope="col" className={s.th}>
              tokens
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map(([label, tokens, modo, total]) => (
            <tr key={label} className={total ? s.total : undefined}>
              <th scope="row" className={mergeClasses(s.td, s.tdLeft)}>
                {label}
              </th>
              <td className={s.td}>{formatCreditos(tokens, modo)}</td>
              <td className={s.td}>{formatTokens(tokens)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        className={s.bar}
        role="progressbar"
        aria-label="Uso de la bolsa del mes"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(saldo.pct)}
      >
        <div className={mergeClasses(s.barFill, barClass)} style={{ width: `${Math.min(100, saldo.pct)}%` }} />
      </div>
      <p className={s.barNote}>{Math.round(saldo.pct)} % de la bolsa del mes</p>

      {loading && !detalle && <p className={s.note}>Cargando consumo…</p>}
      {funciones.length > 0 && (
        <>
          <h3 className={s.section}>Consumo por función</h3>
          <table className={s.table}>
            <caption style={{ position: 'absolute', left: '-9999px' }}>Consumo por función en créditos y tokens</caption>
            <thead>
              <tr>
                <th scope="col" className={mergeClasses(s.th, s.thLeft)}>
                  Función
                </th>
                <th scope="col" className={s.th}>
                  créditos
                </th>
                <th scope="col" className={s.th}>
                  tokens
                </th>
              </tr>
            </thead>
            <tbody>
              {funciones.map((f) => (
                <tr key={f.id}>
                  <th scope="row" className={mergeClasses(s.td, s.tdLeft)}>
                    {f.label}
                  </th>
                  <td className={s.td}>{formatCreditos(f.tokens, 'desglose')}</td>
                  <td className={s.td}>{formatTokens(f.tokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {recargas.length > 0 && (
        <>
          <h3 className={s.section}>Recargas</h3>
          <ul className={s.eq}>
            {recargas.slice(0, 4).map((r, i) => (
              <li key={`${r.fecha}-${i}`}>
                {r.etiqueta || 'Recarga'} · {etiquetaCreditos(r.tokens)} ({formatTokens(r.tokens)} tokens)
              </li>
            ))}
          </ul>
        </>
      )}

      {promedios.length > 0 && (
        <>
          <h3 className={s.section}>Cuánto suele costar</h3>
          <ul className={s.eq}>
            {promedios
              .filter((p) => ['ia_completa', 'apu', 'chat', 'procedimiento'].includes(p.id))
              .map((p) => (
                <li key={p.id}>
                  {p.label}: {p.texto}
                </li>
              ))}
          </ul>
        </>
      )}

      <p className={s.note}>
        {NOTA_EQUIVALENCIA}. Primero se usa la bolsa del mes y luego las recargas, que no vencen. Se cobra el consumo real:
        los promedios son una guía, no una tarifa fija.
      </p>
      {onBuy && (
        <div className={s.actions}>
          <Button appearance="primary" size="small" onClick={onBuy}>
            Comprar recarga
          </Button>
        </div>
      )}
    </div>
  );
}

export function CreditosChip({
  className,
  onBuy,
  variante = 'disponible',
}: {
  className?: string;
  onBuy?: () => void;
  /** 'disponible' → píldora «◔ 812 créditos» (header); 'solo' → "812 créditos"; 'usado' → "188 de 1,000 créditos". */
  variante?: 'disponible' | 'usado' | 'solo';
}) {
  const s = useStyles();
  const usage = useUsageStore((st) => st.data);
  const [open, setOpen] = useState(false);
  const movil = useMediaQuery('(max-width: 639px)');
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const tituloRef = useRef<HTMLHeadingElement | null>(null);

  const saldo = saldoEnTokens(usage);
  if (!usage || !enCreditos(usage) || !saldo) return null;

  const color = saldo.pct >= 90 ? 'var(--bad)' : saldo.pct >= 75 ? 'var(--warn)' : 'var(--text-primary)';
  const texto =
    variante === 'usado'
      ? `${formatCreditos(saldo.usado, 'saldo')} de ${etiquetaCreditos(saldo.bolsa)}`
      : etiquetaCreditos(saldo.disponible);
  const aria =
    variante === 'usado'
      ? `${formatCreditos(saldo.usado, 'saldo')} de ${etiquetaCreditos(saldo.bolsa)} usados. Ver desglose`
      : `${etiquetaCreditos(saldo.disponible)} disponibles de ${etiquetaCreditos(saldo.bolsa + saldo.recargas)}. Ver desglose`;

  const cerrar = () => {
    setOpen(false);
    // El foco vuelve al botón (Fluent lo hace con Esc; con la hoja se hace a mano).
    requestAnimationFrame(() => btnRef.current?.focus());
  };
  const comprar = onBuy
    ? () => {
        setOpen(false);
        onBuy();
      }
    : undefined;

  const pastel = pastelDelSaldo(saldo);
  const cifra = formatCreditos(saldo.disponible, 'saldo');
  const boton = variante === 'disponible' ? (
    <button
      ref={btnRef}
      type="button"
      className={s.pill}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={aria}
      onClick={movil ? () => setOpen((o) => !o) : undefined}
    >
      <span
        className={s.dot}
        aria-hidden="true"
        style={
          {
            '--cr-pct': `${pastel.pct}%`,
            '--cr-pie': pastel.nivel === 'bad' ? 'var(--bad)' : pastel.nivel === 'warn' ? 'var(--warn)' : 'var(--accent)',
          } as CSSProperties
        }
      />
      <span>
        {cifra}
        <span className={s.unidad}> {cifra === '1' ? 'crédito' : 'créditos'}</span>
      </span>
    </button>
  ) : (
    <button
      ref={btnRef}
      type="button"
      className={mergeClasses(className, s.btn)}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={aria}
      onClick={movil ? () => setOpen((o) => !o) : undefined}
    >
      <strong style={{ color }}>{texto}</strong>
    </button>
  );

  if (movil) {
    return (
      <>
        {boton}
        <OverlayDrawer
          position="bottom"
          open={open}
          onOpenChange={(_, d) => (d.open ? setOpen(true) : cerrar())}
          style={{ height: 'auto', maxHeight: '88vh' }}
          aria-label="Tus créditos"
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={<Button appearance="subtle" aria-label="Cerrar" icon={<Dismiss24Regular />} onClick={cerrar} />}
            >
              Tus créditos
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody className={s.drawerBody}>
            <CreditosDesglose onBuy={comprar} ocultarTitulo />
          </DrawerBody>
        </OverlayDrawer>
      </>
    );
  }

  return (
    <Popover
      withArrow
      positioning="below"
      trapFocus
      open={open}
      onOpenChange={(_, d) => {
        setOpen(d.open);
        if (d.open) requestAnimationFrame(() => tituloRef.current?.focus());
      }}
    >
      <PopoverTrigger disableButtonEnhancement>{boton}</PopoverTrigger>
      <PopoverSurface className={s.pop} role="dialog" aria-label="Tus créditos">
        <CreditosDesglose onBuy={comprar} tituloRef={tituloRef} onClose={cerrar} />
      </PopoverSurface>
    </Popover>
  );
}
